import React, { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams } from 'react-router-dom';
import { Typography , Box , Divider } from '@mui/material';

import Header from 'components/Analytics/Header'
import ScreenHeading from 'components/ScreenHeading'
import Tabs from 'components/Tabs'
import Usage from 'components/Analytics/Usage'
import Trends from 'components/Analytics/Trends'
import BatchHighlighting from 'components/Analytics/BatchHighlighting'
import LeaderBoard from 'components/Analytics/LeaderBoard'
import RecentTemplatesAnalytics from 'components/BatchAnalytics/RecentTemplatesAnalytics';
import StudentsPerformanceList from 'components/Analytics/StudentsPerformanceList';
import { SimpleTabs } from 'components/Tabs'
import { WhiteBox } from 'components/AppShell'
import { getBatchById } from 'services';
import { getLeaderboard } from 'services/analyticsV2';
import { isCaseVariant } from 'components/Analytics/constant';
 
const CHALLENGES = [
  { _id: 'assessment', label: 'Assessment' },
  { _id: 'mockInterview', label: 'Mock Interview' },
  { _id: 'interviewWizard', label: 'Interview Wizard' },
];

const TAB_KEYS = CHALLENGES.map(challenge => challenge._id);

const TOGGLE_OPTIONS = [
  { _id: 'nextRoundMock', label: 'Next Round Mock' },
  { _id: 'codingMock', label: 'Coding Mock' },
  { _id: 'caseSolving', label: 'Case Solving' },
]

// (tab, toggle) -> the variant the API is asked for.
const VARIANT_BY_SECTION = {
  mockInterview: {
    nextRoundMock: 'verbal_mock',
    codingMock: 'coding_mock',
    caseSolving: 'case_mock',
  },
  interviewWizard: {
    nextRoundMock: 'verbal_iw',
    codingMock: 'coding_iw',
    caseSolving: 'case_iw',
  },
};

function InfoBox({ interviewVariantType }) {
  const hint = isCaseVariant(interviewVariantType)
    ? 'Use the filter to view the top 5 performers in "High Scorers" and "Most Dedicated", or the bottom 5 in "Low Score".'
    : 'Use the filter to view top 5 performers in "High Scorers" and "Most Dedicated" categories.';

  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor="#F2FAFF"
      borderRadius={2}
      px={2}
      py={1.5}
      mt={1}
      width="50%"
    >
      <InfoOutlinedIcon sx={{ color: 'primary.clr-600', fontSize: 16, mr: 1 }} />
      <Typography
        variant="body01-semiBold"
        color="neutral.clr-600"
      >
        {hint}
      </Typography>
    </Box>
  );
}

function BatchAnalytics() {
  const { id } = useParams();

  const [_batch, setBatch] = React.useState({});
  const [activeTab, setActiveTab] = React.useState('interviewWizard');
  const [section, setSection] = React.useState('nextRoundMock');
  const [leaderboardCategory, setLeaderboardCategory] = useState('best_score');

  const [leaderboardData, setLeaderboardData] = React.useState({ topPerformers: [], bestPerformers: [] });
  const [leaderboardLoading, setLeaderboardLoading] = React.useState(true);
  const [leaderboardError, setLeaderboardError] = React.useState(null);

  const interviewVariantType = React.useMemo(
    () => VARIANT_BY_SECTION[activeTab]?.[section] || 'assessment',
    [activeTab, section]
  );

  // Recent published items only exist for batch-published mocks, never for the
  // self-driven Interview Wizard.
  const showRecentTemplates = activeTab === 'mockInterview';

  const handleTabChange = (tabId) => {
    setActiveTab(TAB_KEYS[tabId]);
    setSection('nextRoundMock');
  };

  const handleToggleChange = (toggleId) => {
    setSection(toggleId);
  };

  const simpleTabsOptions = React.useMemo(
    () => CHALLENGES.map(({ _id, label }) => ({ id: _id, label })),
    []
  );

  React.useEffect(() => {
    getBatchById(id, { domain: true })
      .then(template => setBatch(template))
      .catch(console.error)
  }, [id]);

  React.useEffect(() => {
    if (!id || !interviewVariantType) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    
    const params = { type: 'batch', id, interviewVariantType };
      params.leaderboardType = leaderboardCategory;
    
    getLeaderboard(params)
      .then(response => {
          setLeaderboardData(response || []);
      })
      .catch(err => setLeaderboardError(err.message))
      .finally(() => setLeaderboardLoading(false));
  }, [id, interviewVariantType, leaderboardCategory]);

  const renderHeaderAndLeaderboards = (variantType) => (
    <>
      <Box display="flex" gap={4} mt={2}>
        <LeaderBoard
          data={leaderboardData}
          loading={leaderboardLoading}
          error={leaderboardError}
          leaderboardCategory={leaderboardCategory}
          setLeaderboardCategory={setLeaderboardCategory}
          interviewVariantType={variantType}
          batchId={id}
          scoreDecimals={isCaseVariant(variantType) ? 2 : 0}
        />
        <Usage batchId={id} interviewVariantType={variantType} />
      </Box>
      <InfoBox interviewVariantType={variantType} />
      <Divider />
      <Trends batchId={id} interviewVariantType={variantType} />
      <Divider />
      <BatchHighlighting batchId={id} interviewVariantType={variantType} />
    </>
  );

  return (
    <Box display="flex" flexDirection="column" minHeight='calc(100vh - 76px)'>
       <ScreenHeading title="Batch Performance" id="batches-performance"></ScreenHeading>
      <WhiteBox>
        <Header batchId={id} />
        <Box display="flex" flexDirection="column" gap={7} mt={3}>
          <SimpleTabs
            options={simpleTabsOptions.map((opt, idx) => ({ ...opt, value: idx }))}
            value={TAB_KEYS.indexOf(activeTab)}
            onChange={handleTabChange}
          />

          {activeTab === 'assessment' && (
            <Box display="flex" flexDirection="column" gap={9}>
              {renderHeaderAndLeaderboards('assessment')}
              <Divider />
              <RecentTemplatesAnalytics batchId={id} interviewVariantType="assessment" />
              <Divider />
              <StudentsPerformanceList batchId={id} interviewVariantType="assessment" />
            </Box>
          )}
          {(activeTab === 'mockInterview' || activeTab === 'interviewWizard') && (
            <Box display="flex" flexDirection="column" gap={9}>
              <Tabs
                options={TOGGLE_OPTIONS}
                value={section}
                onChange={handleToggleChange}
                width='190px'
              />
              {renderHeaderAndLeaderboards(interviewVariantType)}
              {showRecentTemplates
                && <>
                    <Divider />
                    <RecentTemplatesAnalytics
                      batchId={id}
                      interviewVariantType={interviewVariantType}
                    />
                  </>
              }
              <Divider />
              <StudentsPerformanceList batchId={id} interviewVariantType={interviewVariantType} />
            </Box>
          )}
        </Box>
      </WhiteBox>
      </Box>
  );
}

export default BatchAnalytics;